/**
 * Fireflies hug walls on their left, butterflies on their right (docs/spec/02 §5). Before moving,
 * each looks at its four neighbours: Digby or amoeba there sets it off.
 */

import { CDX, CDY, E } from '../elements';
import { at, isSpace, move, put } from '../grid';
import type { CaveState, Dir } from '../types';
import { DIR_DOWN, DIR_LEFT, DIR_RIGHT, DIR_UP } from '../types';
import { explode } from './explode';

/** Creature heading (left, up, right, down) as an input direction, for `move`. */
const AS_DIR: readonly Dir[] = [DIR_LEFT, DIR_UP, DIR_RIGHT, DIR_DOWN];

function touchesTrigger(s: CaveState, x: number, y: number): boolean {
  for (let d = 0; d < 4; d++) {
    const el = at(s, x + (CDX[d] ?? 0), y + (CDY[d] ?? 0));
    if (el === E.DIGBY || el === E.AMOEBA) return true;
  }
  return false;
}

/**
 * `first` is the turn tried first: +3 (left) for a firefly, +1 (right) for a butterfly. Failing
 * that it goes ahead; failing that it turns the other way on the spot.
 */
function creature(s: CaveState, x: number, y: number, base: number, heading: number, first: number): void {
  if (touchesTrigger(s, x, y)) {
    explode(s, x, y, base === E.BUTTERFLY ? 'diamonds' : 'space');
    return;
  }
  const turned = (heading + first) % 4;
  if (isSpace(s, x + (CDX[turned] ?? 0), y + (CDY[turned] ?? 0))) {
    move(s, x, y, AS_DIR[turned] ?? DIR_LEFT, base + turned);
    return;
  }
  if (isSpace(s, x + (CDX[heading] ?? 0), y + (CDY[heading] ?? 0))) {
    move(s, x, y, AS_DIR[heading] ?? DIR_LEFT, base + heading);
    return;
  }
  put(s, x, y, base + ((heading + 4 - first) % 4));
}

export function firefly(s: CaveState, x: number, y: number, el: number): void {
  creature(s, x, y, E.FIREFLY, el - E.FIREFLY, 3);
}

export function butterfly(s: CaveState, x: number, y: number, el: number): void {
  creature(s, x, y, E.BUTTERFLY, el - E.BUTTERFLY, 1);
}
